import useGetMyAccountFeatures from '@/api/queries/me/useGetMyAccountFeatures';
import useGetMyAccountGrants from '@/api/queries/me/useGetMyAccountGrants';
import useGetMyAccountSettings from '@/api/queries/me/useGetMyAccountSettings';
import useGetProjectByIdOverview from '@/api/queries/project/useGetProjectByIdOverview';
import {
  useGetProposalSections,
  useSelectProposal,
} from '@/api/queries/proposal/useGetProposal';
import { GetProposalQuery } from '@/gql/graphql';
import { usePermissions } from '@/hooks/usePermissions';
import { useChatAgent } from '@/ui/components/chatAgent/ChatAgentContextProvider';
import PreviewBuilderLayout from '@/ui/layouts/previewBuilder/PreviewBuilderLayout';
import { legacyProposalPath } from '@/ui/pages/legacyProposalPage/legacyProposalPath';
import AttachmentsTab from '@/ui/pages/proposalBuilderPage/components/AttachmentsTab';
import GrantsTab from '@/ui/pages/proposalBuilderPage/components/GrantsTab';
import SendProposalView from '@/ui/pages/proposalBuilderPage/components/SendProposalView';
import PaymentTab from '@/ui/pages/proposalBuilderPage/components/PaymentTab';
import ProposalActionsMenu from '@/ui/pages/proposalBuilderPage/components/ProposalActionsMenu';
import ProposalHeaderIdentity from '@/ui/pages/proposalBuilderPage/components/ProposalHeaderIdentity';
import ProposalEditorSkeleton from '@/ui/pages/proposalBuilderPage/components/ProposalEditorSkeleton';
import ProposalFinancials from '@/ui/pages/proposalBuilderPage/components/ProposalFinancials';
import ProposalItemEditor from '@/ui/pages/proposalBuilderPage/components/ProposalItemEditor';
import ProposalItemGroupTab from '@/ui/pages/proposalBuilderPage/components/ProposalItemGroupTab';
import ProposalItemPicker from '@/ui/pages/proposalBuilderPage/components/ProposalItemPicker';
import ProposalPreviewPane from '@/ui/pages/proposalBuilderPage/components/ProposalPreviewPane';
import SentProposalPanel from '@/ui/pages/proposalBuilderPage/components/sentProposalPanel/SentProposalPanel';
import RichtextContentTab from '@/ui/pages/proposalBuilderPage/components/RichtextContentTab';
import { type ProposalDocumentRegion } from '@/ui/pages/proposalBuilderPage/useProposalDocumentPreview';
import { proposalSectionsHaveLineItem } from '@/ui/pages/proposalBuilderPage/util';
import {
  AnchorButton,
  BreadcrumbItem,
  FloatingTooltip,
  TooltipCore,
} from '@payaca/ambrosia';
import RouterTabs, { TabPanel } from '@payaca/components/routerTabs/RouterTabs';
import { LabourForecastPermissions } from '@payaca/permissions/labourForecast/labourForecast.permissions';
import { MaterialsListPermissions } from '@payaca/permissions/materialsList/materialsList.permissions';
import UntitledIcon from '@payaca/untitled-icons';
import { FC, MouseEvent, useCallback, useState } from 'react';
import {
  Link,
  Redirect,
  useHistory,
  useParams,
  useRouteMatch,
} from 'react-router-dom';

const selectBuilderProposal = (proposal: GetProposalQuery['proposal']) => ({
  sentAt: proposal.sentAt,
  reference: proposal.customReference ?? proposal.reference,
  hasGrants: proposal.grants.length > 0,
  total: proposal.totals.total.value,
});

type ProposalData = ReturnType<typeof selectBuilderProposal> | undefined;
type ProjectData = ReturnType<typeof useGetProjectByIdOverview>['project'];
type AccountData = ReturnType<typeof useGetMyAccountSettings>['account'];

type ProposalBuilderPreviewProps = {
  proposalId: string;
  isSent: boolean;
  /** Closes the preview sheet, so a click on mobile reveals the editor. */
  closePanel: () => void;
  /** Opens the editor panel, which the Sales Agent may have collapsed. */
  openPanel: () => void;
  isMobile: boolean;
};

/**
 * The preview pane, wired to the builder's routes: a click in the document
 * navigates to the section or line item it belongs to.
 */
const ProposalBuilderPreview: FC<ProposalBuilderPreviewProps> = (props) => {
  const { proposalId, isSent, closePanel, openPanel, isMobile } = props;

  const history = useHistory();
  const { url, path } = useRouteMatch();
  // Deliberately not exact, so editing a line item still reports the section
  // that item belongs to and the preview stays put.
  const activeTab = useRouteMatch<{ tabId: string }>({
    path: `${path}/:tabId`,
  });
  const isDraftAndSending = useRouteMatch({
    path: `${path}/draft-send`,
    exact: true,
  });

  const handleRegionClick = useCallback(
    (region: ProposalDocumentRegion) => {
      history.push(
        region.type === 'lineItem'
          ? `${url}/${region.sectionKey}/${region.proposalLineItemId}`
          : `${url}/${region.sectionKey}`
      );
      // On mobile the preview covers the editor it just navigated; on desktop
      // the editor may be collapsed to nothing.
      if (isMobile) closePanel();
      else openPanel();
    },
    [history, url, isMobile, closePanel, openPanel]
  );

  // On the "draft and send" screen, navigating away would discard the drafted
  // email, so the navigation by clicking the preview is disabled.
  const isEditingSections = !isSent && !isDraftAndSending;

  return (
    <ProposalPreviewPane
      proposalId={proposalId}
      onRegionClick={isEditingSections ? handleRegionClick : undefined}
      activeSectionKey={activeTab?.params.tabId ?? null}
    />
  );
};

type ProposalBuilderEditorProps = {
  proposalId: string;
  projectId: string;
  proposal: ProposalData;
  project: ProjectData;
  account: AccountData;
  /** Whether the Account can use grants AND has any to show on this Proposal. */
  showGrantsTab: boolean;
  isLoading: boolean;
};

const ProposalBuilderEditor: FC<ProposalBuilderEditorProps> = (props) => {
  const {
    proposalId,
    projectId,
    proposal,
    project,
    account,
    showGrantsTab,
    isLoading,
  } = props;

  const { url, path } = useRouteMatch();
  const isAddingItem = useRouteMatch<{
    tabId: string;
    groupKey: string;
  }>({
    path: `${path}/:tabId/:groupKey/add-item`,
    exact: true,
  });
  const isEditingItem = useRouteMatch<{
    tabId: string;
    itemId: string;
  }>({
    path: `${path}/:tabId/:itemId`,
    exact: true,
  });
  const isDraftAndSending = useRouteMatch({
    path: `${path}/draft-send`,
    exact: true,
  });

  const { proposalSections } = useGetProposalSections(proposalId);

  const hasLineItem = proposalSectionsHaveLineItem(proposalSections ?? []);

  const { userHasRequiredPermission } = usePermissions();
  const canSeeMaterialsList = userHasRequiredPermission([
    MaterialsListPermissions.GET_MATERIALS_LISTS,
  ]);
  const canSeeLabourForecast = userHasRequiredPermission([
    LabourForecastPermissions.GET_LABOUR_FORECAST,
  ]);

  // The builder's forms seed once from the server and then own their value, so
  // they only follow a write made elsewhere when they remount. Both writers that
  // can make one are explicit signals: applying a Template, and Agent Dave
  // finishing a mutating tool call. An autosave bumps neither, so a form's own
  // refetch can never reset an editor and clobber in-progress typing.
  const [templateRevision, setTemplateRevision] = useState(0);
  const handleTemplateApplied = useCallback(
    () => setTemplateRevision((revision) => revision + 1),
    []
  );
  const { agentWriteRevision } = useChatAgent();
  const seedRevision = `${templateRevision}-${agentWriteRevision}`;

  // Held above ProposalFinancials so the summary keeps its expanded state when
  // that editor remounts — e.g. while navigating to add an item and back.
  const [financialsOpen, setFinancialsOpen] = useState(false);

  if (isLoading) {
    return <ProposalEditorSkeleton />;
  }
  // Once sent, there are no sections to author, so the panel becomes the
  // Proposal's actions. Checked before the send route, so a stale link to it
  // lands on those actions rather than on a composer for a send that happened.
  if (proposal?.sentAt) {
    return <SentProposalPanel proposalId={proposalId} projectId={projectId} />;
  }
  // Not keyed on seedRevision: it holds an unsent email draft that a remount
  // would discard, so the one field inside that follows the server keys itself.
  if (isDraftAndSending) {
    return <SendProposalView back={{ to: url }} />;
  }
  if (isAddingItem) {
    return (
      <ProposalItemPicker
        proposalId={proposalId}
        groupKey={isAddingItem.params.groupKey}
        backTo={`${url}/${isAddingItem.params.tabId}`}
      />
    );
  }
  if (isEditingItem) {
    return (
      <ProposalItemEditor
        key={`${proposalId}-${isEditingItem.params.itemId}`}
        proposalId={proposalId}
        proposalLineItemId={isEditingItem.params.itemId}
        backTo={`${url}/${isEditingItem.params.tabId}`}
      />
    );
  }

  // Items leads the tabs, so the builder opens on the work the Sales Agent came
  // to do. The content document keeps its own order — that is the order the
  // Customer reads the Proposal in, and it renders from the same array.
  const orderedSections = [
    ...(proposalSections ?? []).filter(
      (section) => section.__typename === 'ProposalItemGroupSection'
    ),
    ...(proposalSections ?? []).filter(
      (section) => section.__typename !== 'ProposalItemGroupSection'
    ),
  ];

  const panels: TabPanel[] = [
    ...orderedSections.map((section) => ({
      label: section.title ?? '',
      slug: section.key,
      render: () =>
        section.__typename === 'ProposalContentSection' ? (
          <RichtextContentTab
            key={`${section.key}-${seedRevision}`}
            proposalId={proposalId}
            section={section}
          />
        ) : (
          <ProposalItemGroupTab proposalId={proposalId} section={section} />
        ),
    })),
    // Before Payment, because a grant feeds the payment schedule.
    ...(showGrantsTab
      ? [
          {
            label: 'Grants',
            slug: 'grants',
            render: () => (
              <GrantsTab proposalId={proposalId} projectId={projectId} />
            ),
          },
        ]
      : []),
    {
      label: 'Payment',
      slug: 'payment',
      render: () => (
        <PaymentTab
          key={seedRevision}
          proposalId={proposalId}
          projectId={projectId}
          proposalTotal={proposal?.total ?? 0}
          project={project}
          account={account}
        />
      ),
    },
    {
      label: 'Attachments',
      slug: 'attachments',
      render: () => <AttachmentsTab proposalId={proposalId} />,
    },
  ];

  return (
    <div className="h-full flex flex-col">
      <div className="p-3.5 border-b flex justify-between items-center gap-2">
        <div className="flex items-center gap-2 min-w-0">
          <ProposalHeaderIdentity proposalId={proposalId} />

          <ProposalActionsMenu
            proposalId={proposalId}
            projectId={projectId}
            onTemplateApplied={handleTemplateApplied}
          />
        </div>

        {/* Gated with `aria-disabled` rather than AnchorButton's `disabled`,
          which swaps the anchor for a span the keyboard cannot reach — and the
          tooltip with it. */}
        <FloatingTooltip enabled={!hasLineItem}>
          <AnchorButton
            as={Link}
            to={`${url}/draft-send`}
            size="sm"
            aria-disabled={!hasLineItem}
            onClick={(event: MouseEvent<HTMLAnchorElement>) => {
              if (!hasLineItem) event.preventDefault();
            }}
          >
            <UntitledIcon name="send-01.3" className="size-4" />
            Next
          </AnchorButton>

          <TooltipCore>Add an item to continue</TooltipCore>
        </FloatingTooltip>
      </div>
      <div className="overflow-y-auto min-h-0 h-full flex flex-col">
        <ProposalFinancials
          proposalId={proposalId}
          projectId={projectId}
          editMaterialsTo={
            canSeeMaterialsList ? `/projects/${projectId}/materials` : undefined
          }
          editLabourTo={
            canSeeLabourForecast ? `/projects/${projectId}/labour` : undefined
          }
          defaultOpen={financialsOpen}
          onOpenChange={setFinancialsOpen}
        />
        <div className="flex-1 min-h-0">
          <RouterTabs panels={panels}>
            <div className="flex h-full min-h-0 flex-col">
              <RouterTabs.Nav
                variant="pilled"
                className="p-3.5 flex-none"
                defaultTabSlug={panels[0]?.slug}
              />
              <div className="min-h-0 flex-1">
                <RouterTabs.Panels />
              </div>
            </div>
          </RouterTabs>
        </div>
      </div>
    </div>
  );
};

const ProposalBuilderPage: FC = () => {
  const { projectId, proposalId } = useParams<{
    projectId: string;
    proposalId: string;
  }>();

  const { project, isLoading: isProjectLoading } =
    useGetProjectByIdOverview(projectId);
  const { data: proposal, isLoading: isProposalLoading } = useSelectProposal(
    proposalId,
    selectBuilderProposal
  );
  const { account, isLoading: isAccountLoading } = useGetMyAccountSettings();

  // Grants hide entirely rather than showing an empty tab: the mutations behind
  // them are gated on the same Account feature, so without it the tab could only
  // ever fail. The catalogue is not fetched until the flag is on.
  const { data: accountFeatures, isLoading: isFeaturesLoading } =
    useGetMyAccountFeatures();
  const hasGrantsFeature =
    accountFeatures?.includes('hasGrantsOnProposals') ?? false;
  const { grants: grantCatalogue, isLoading: isGrantCatalogueLoading } =
    useGetMyAccountGrants({ enabled: hasGrantsFeature });

  // A sent Proposal never reaches the tab strip at all — the editor becomes
  // SentProposalPanel — so this only ever decides the draft case.
  const showGrantsTab =
    hasGrantsFeature && (grantCatalogue.length > 0 || !!proposal?.hasGrants);

  // Also fixes the initial tab: a tab whose existence is still being resolved is
  // not in `panels` yet, and RouterTabs redirects away from a slug it cannot
  // find. That loses a deep link to the section tabs (they live in the proposal
  // query) and to Grants (the feature and catalogue queries).
  const isEditorLoading =
    isProposalLoading ||
    isProjectLoading ||
    isAccountLoading ||
    isFeaturesLoading ||
    isGrantCatalogueLoading;

  // A legacy Project's Proposals are no longer authored or rendered anywhere.
  if (project && project.version < 2) {
    return <Redirect to={legacyProposalPath(proposalId, projectId)} />;
  }

  const breadcrumbItems: BreadcrumbItem[] = [
    {
      label: project?.pipeline.name ?? 'Pipeline',
      to: project?.pipeline
        ? `/pipelines/${project.pipeline.publicId}`
        : undefined,
    },
    {
      label: project ? `Project: #${project.reference}` : 'Project',
      to: `/projects/${projectId}/proposals`,
    },
    {
      label: proposal ? `Proposal: #${proposal.reference}` : 'Proposal',
    },
  ];

  return (
    <PreviewBuilderLayout
      breadcrumbItems={breadcrumbItems}
      previewButtonLabel="Preview proposal"
      renderEditor={() => (
        // Keyed so navigating between Proposals on one route — a duplicate or
        // an edit lands on a copy — remounts the editor rather than rendering
        // the new Proposal into the old one's state.
        <ProposalBuilderEditor
          key={proposalId}
          proposalId={proposalId}
          projectId={projectId}
          proposal={proposal}
          project={project}
          account={account}
          showGrantsTab={showGrantsTab}
          isLoading={isEditorLoading}
        />
      )}
      renderPreview={({ isMobile, closePanel, openPanel }) => (
        <ProposalBuilderPreview
          proposalId={proposalId}
          isSent={!!proposal?.sentAt}
          closePanel={closePanel}
          openPanel={openPanel}
          isMobile={isMobile}
        />
      )}
    />
  );
};

export default ProposalBuilderPage;
